import i18n, { type Resource } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

/**
 * Shared i18next setup. Apps pass bundled `en` as `resources` so SSR and first
 * client paint render the same text (avoiding a hydration mismatch); on the
 * client, http-backend loads other languages and language-detector picks one.
 */
export function initI18n(resources?: Resource): typeof i18n {
	if (i18n.isInitialized) return i18n;

	const isBrowser = typeof document !== 'undefined';
	const instance = i18n.use(initReactI18next);
	if (isBrowser) {
		instance.use(HttpBackend).use(LanguageDetector);
	}

	void instance.init({
		resources,
		// Initialise synchronously so t() works the instant init() returns — apps
		// call initI18n() at module scope without awaiting, and SSR must not render
		// before the bundled resources are in place. http-backend loads the rest async.
		initImmediate: false,
		// Let http-backend top up namespaces alongside the bundled ones.
		partialBundledLanguages: true,
		fallbackLng: 'en',
		lng: isBrowser ? undefined : 'en',
		// Only 'en' ships today; map regional variants (en-GB) to it and never
		// request an unsupported locale file.
		supportedLngs: ['en'],
		load: 'languageOnly',
		defaultNS: 'common',
		ns: ['common'],
		interpolation: { escapeValue: false },
		backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
		detection: { caches: ['localStorage', 'cookie'] },
		react: { useSuspense: false },
	});

	return i18n;
}

/**
 * The global `t` for copy needed OUTSIDE React render — notably React Router `meta()`
 * functions, which set the document title / SEO description but run without hooks. Uses
 * the initialised singleton (apps call initI18n() synchronously at module scope) and
 * falls back to `fallback` (the English literal) so a title still renders if i18n hasn't
 * loaded yet. Interpolation vars pass through the third arg.
 */
export function metaT(key: string, fallback: string, vars?: Record<string, unknown>): string {
	return i18n.t(key, { defaultValue: fallback, ...vars });
}
